import Button from '@alltrails/shared/denali/components/Button';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Close from '@alltrails/shared/icons/Close';
import Typography from '@alltrails/shared/denali/components/Typography';
import PortalModal from 'components/shared/PortalModal/PortalModal';
import useIsMobile from '@alltrails/shared/hooks/useIsMobile';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import InstallPromptLocation from '@alltrails/analytics/enums/InstallPromptLocation';
import InstallPromptType from '@alltrails/analytics/enums/InstallPromptType';
import logInstallPromptViewed from '@alltrails/analytics/events/logInstallPromptViewed';
import { useEffect } from 'react';
import InstallPromptSource from '@alltrails/analytics/enums/InstallPromptSource';
import logInstallPromptDismissed from '@alltrails/analytics/events/logInstallPromptDismissed';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import * as styles from './styles/styles.module.scss';
import useDeeplink from '../hooks/useDeeplink';
import { deepLinkRedirect } from '../utils';

type BannerProps = {
  promptLocation: InstallPromptLocation;
  pageId?: number;
  pageType?: 'area' | 'trail';
  isOpen?: boolean;
  onRequestClose?: () => void;
  displayTrigger?: CarouselDisplayTrigger;
};

// Design link: https://www.figma.com/file/eQkMBkn2YnhYkVahhaXrZG/In-House-Branch?type=design&node-id=719-23003&mode=design&t=4MtkLwmxzWL94uYe-0

const Banner = ({ pageId, pageType, promptLocation, isOpen = false, displayTrigger, onRequestClose }: BannerProps) => {
  const isMobile = useIsMobile();
  const intl = useIntl();
  const ctaText = intl.formatMessage({ defaultMessage: 'Get app' });
  const deeplink = useDeeplink(pageType, pageId);

  useEffect(() => {
    if (isOpen && isMobile) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: InstallPromptType.Banner,
        install_prompt_source: InstallPromptSource.Native,
        signup_modal_trigger: displayTrigger
      });
    }
  }, [displayTrigger, isOpen, promptLocation, isMobile]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: InstallPromptType.Banner,
      install_prompt_source: InstallPromptSource.Native
    });
    onRequestClose();
  };

  return (
    <PortalModal isOpen={isOpen && isMobile}>
      <div className={styles.container}>
        <div className={styles.buttonAndTextContainer}>
          <IconButton
            className={styles.close}
            icon={{ Component: Close }}
            onClick={handleCloseClick}
            size="sm"
            title="close"
            testId="close-button"
            variant="flat"
          />
          <Typography variant="text200bold" className={styles.text}>
            <FormattedMessage defaultMessage="Try more features for free" />
          </Typography>
        </div>
        <Button
          text={ctaText}
          onClick={() => deepLinkRedirect(deeplink, InstallPromptType.Banner, promptLocation)}
          className={styles.button}
          aria-label="Get app"
          testId="banner-button"
          variant="elevated"
          size="sm"
        />
      </div>
    </PortalModal>
  );
};

export default Banner;
